import * as React from 'react'
import { useState, useEffect } from 'react';
import VariableComponent from "./VariableComponent";
import PartsList from "./PartsList"
import PartBuilder from './PartBuilder';
import { EditorState, convertToRaw, ContentState } from 'draft-js'
import htmlToDraft from 'html-to-draftjs';
import draftToHtml from 'draftjs-to-html';
import WarningModalComponent from './WarningModalComponent'
import LinksList from './LinksList'
import { CPPart } from '../models/CPPart';
import { CPProblem } from '../models/CPProblem';
import { CPImage } from '../models/CPImage';
import { CPImageAlignment } from '../enums/CPImageAlignment';
var deepEqual = require('deep-equal');
import CreateableSelect from "react-select/creatable"
import { CPVariable } from '../models/CPVariable';
import Select from 'react-select';
import { CPImageSize } from '../enums/CPImageSize';
import { CPReactMethods } from '../app';
import ConfirmationModalComponent from './ConfirmationModalComponent';
import BreadcrumbComponent from './BreadcrumbComponent';
import RichTextEditorComponent from './RichTextEditorComponent';
const cloneDeep = require('lodash.clonedeep');

export interface ProblemBuilderProps {
    problem: CPProblem
    assignmentId?: number
    userIsOwner: boolean
    limitEditing: boolean
    inMultiplePlaces: boolean
    answeredInAssignment?: boolean
    returnProblemToAssignment?: Function
    standAloneProblem: boolean
    assignmentLimitedEditing?: boolean
    assignmentIsShared?: boolean
    removePartInstance?: Function
    partsCount?: number //To force update on part instance deletion
}

const difficultyOptions = [{ label: "Very Easy", value: 1 },
{ label: "Easy", value: 2 },
{ label: "Medium", value: 3 },
{ label: "Hard", value: 4 },
{ label: "Very Hard", value: 5 }]

function ProblemBuilder(props: ProblemBuilderProps) {
    const [savingProblem, setSavingProblem] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [initialCPProblem, setInitialCPProblem] = useState<CPProblem>(props.problem)
    const [id, setId] = useState<number | undefined>(props.problem.id)
    const [name, setName] = useState<string>(props.problem.name)
    const [parts, setParts] = useState<Array<CPPart>>(props.problem.parts || [])
    const [selectedPartIndex, setSelectedPartIndex] = useState<number | undefined>(undefined)
    const [image, setImage] = useState<CPImage>(props.problem.image)
    const [variables, setVariables] = useState<Array<CPVariable>>(props.problem.variables || [])
    const [text, setText] = useState(props.problem.text || "")
    var partTextToBlocks = htmlToDraft(text || "")
    const [textEditorState, setTextEditorState] = useState(
        () => EditorState.createWithContent(ContentState.createFromBlockArray(partTextToBlocks.contentBlocks, partTextToBlocks.entityMap)),
    )
    const [helpText, setHelpText] = useState(props.problem.helpText || "")
    var partHelpTextToBlocks = htmlToDraft(helpText || "")
    const [helpTextEditorState, setHelpTextEditorState] = useState(
        () => EditorState.createWithContent(ContentState.createFromBlockArray(partHelpTextToBlocks.contentBlocks, partHelpTextToBlocks.entityMap)),
    )
    const [links, setLinks] = useState(props.problem.links || [])
    const [limitedEditing, setLimitedEditing] = useState(props.limitEditing || false)
    const [problemInstanceId, setProblemInstanceId] = useState<number | undefined>(props.problem.instanceId)
    const [pendingChanges, setPendingChanges] = useState<boolean>(false)
    var formattedTags: Array<{ label: string, value: string }> = []
    if (props.problem.tags) {
        props.problem.tags.forEach(tag => formattedTags.push({ label: tag, value: tag }))
    }
    const [selectedTags, setSelectedTags] = useState<Array<{ label: string, value: string }>>(formattedTags)
    const [tagOptions, setTagOptions] = useState<Array<{ label: string, value: string }>>([])
    const [userIsOwner, setUserIsOwner] = useState(props.userIsOwner)
    const [inMultiplePlaces, setInMultiplePlaces] = useState(props.inMultiplePlaces);
    const [answeredInAssignment, setAnsweredInAssignment] = useState(props.answeredInAssignment)
    const [assignmentIsShared, setAssignmentIsShared] = useState(props.assignmentIsShared)
    const [difficulty, setDifficulty] = useState(props.problem.difficulty)
    const [warningContent, setWarningContent] = useState("")
    const [lastTimeSaved, setLastTimeSaved] = useState<string | undefined>()
    const [isProblemPreview, setIsProblemPreview] = useState(false)

    useEffect(() => {
        if (initialCPProblem) {
            var formattedInitialCPProblemTags: Array<{ label: string, value: string }> = []
            if (initialCPProblem.tags) {
                initialCPProblem.tags.forEach(tag => formattedInitialCPProblemTags.push({ label: tag, value: tag }))
            }
            if (
                id != initialCPProblem.id ||
                name != initialCPProblem.name ||
                (text != initialCPProblem.text && (text != "" || initialCPProblem.text != null)) ||
                (helpText != initialCPProblem.helpText && (helpText != "" || initialCPProblem.helpText != null)) ||
                problemInstanceId != initialCPProblem.instanceId ||
                difficulty != initialCPProblem.difficulty ||
                !deepEqual(links, initialCPProblem.links) ||
                !deepEqual(image, initialCPProblem.image) ||
                !deepEqual(variables, initialCPProblem.variables) ||
                !deepEqual(parts, initialCPProblem.parts) ||
                !deepEqual(selectedTags, formattedInitialCPProblemTags)) {
                setPendingChanges(true)
            }
            else {
                setPendingChanges(false)
            }
        }
    }, [initialCPProblem, helpText, id, image, links, name, parts.flat(2), text, helpText, variables.flat(2), selectedTags.flat(), difficulty, problemInstanceId])

    useEffect(() => {
        window.CPInstructor.getProblemTags().then(response => {
            var formattedTags: Array<{ label: string, value: string }> = []
            response.forEach(t => {
                formattedTags.push({ label: t, value: t })
            })
            setTagOptions(formattedTags)
        }).catch((error) => {
            setWarningContent(error)
            // @ts-ignore
            var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal"), { backdrop: true, keyboard: true, focus: true })
            warningModal.show();
        })
    }, [])

    const changeImageURL = input => {
        var newImage = cloneDeep(image)
        newImage.url = input.value
        setImage(newImage)
    }

    const changeImageAlign = input => {
        var newImage = cloneDeep(image)
        newImage.align = input.value
        setImage(newImage)
    }

    const changeImageSize = input => {
        var newImage = cloneDeep(image)
        newImage.imageSize = input.value
        setImage(newImage)
    }

    const changeTextEditor = textEditorState => {
        const rawContentState = convertToRaw(textEditorState.getCurrentContent())
        const markup = draftToHtml(
            rawContentState,
        )
        if (markup.length <= 5000) {
            setText(markup)
            setTextEditorState(textEditorState)
        }
    }

    const changeHelpTextEditor = helpTextEditorState => {
        const rawContentState = convertToRaw(helpTextEditorState.getCurrentContent())
        const markup = draftToHtml(
            rawContentState,
        )
        setHelpText(markup)
        setHelpTextEditorState(helpTextEditorState)
    }

    const savePart = (part: CPPart) => {
        var savePartIndex = parts.findIndex(p => p.order == part.order)
        var updatedParts = cloneDeep(parts)
        updatedParts[savePartIndex] = part
        setParts(updatedParts)
        //setSelectedPart(undefined)
    }

    const checkVariableForPossibleValues = (variable: CPVariable) => {
        var possibleValues: Array<number> = []
        variable.rs.forEach(range => {
            for (var value = parseFloat(range.min.toString()); value <= parseFloat(range.max.toString()); value += parseFloat(range.inc.toString())) {
                possibleValues.push(value)
            }
        })
        variable.es.forEach(exclusion => {
            possibleValues.forEach((value, valueIndex) => {
                if (value <= parseFloat(exclusion.max.toString()) && value >= parseFloat(exclusion.min.toString())) {
                    possibleValues.splice(valueIndex, 1)
                }
            })
        })
        if (possibleValues.length == 0) {
            throw "Variable " + variable.name + "'s ranges and exclusions do not create any possible values."
        }
    }

    const checkDependentVariableCalculation = (checkedVariable: CPVariable, variables: Array<CPVariable>) => {
        var openBracketIndexes: Array<number> = []
        var closedBracketIndexes: Array<number> = []
        for (let index = 0; index < checkedVariable.calculation!.length; index++) {
            if (checkedVariable.calculation![index] === '[') {
                openBracketIndexes.push(index);
            }
            else {
                if (checkedVariable.calculation![index] === ']') {
                    closedBracketIndexes.push(index)
                }
            }
        }
        if (openBracketIndexes.length != closedBracketIndexes.length) {
            throw "Dependent variable " + checkedVariable.name + "'s calculation has mismatching brackets in it resulting in incorrect variable names."
        }
        openBracketIndexes.forEach((value, index) => {
            if (closedBracketIndexes[index] < value) {
                throw "Dependent variable " + checkedVariable.name + "'s calculation has mismatching brackets in it resulting in incorrect variable names."

            }
            var independentVariableName = checkedVariable.calculation!.substring(value + 1, closedBracketIndexes[index])
            let foundIndependentVariableName = false
            if (independentVariableName == "g") {
                foundIndependentVariableName = true
            }
            if (!foundIndependentVariableName) {
                for (let i = 0; i < variables.length; i++) {
                    if (!variables[i].isDependent && variables[i].name == independentVariableName) {
                        foundIndependentVariableName = true
                        break
                    }
                }
            }
            if (!foundIndependentVariableName) {
                throw "Dependent variable " + checkedVariable.name + " contains an invalid variable name. Only independent variable names are valid. Variable names are case sensititve"
            }
        })
    }

    const checkEquationForVariableNames = (checkedPart: CPPart, variables: Array<CPVariable>) => {
        var openBracketIndexes: Array<number> = []
        var closedBracketIndexes: Array<number> = []
        for (let index = 0; index < checkedPart.equation!.length; index++) {
            if (checkedPart.equation![index] === '[') {
                openBracketIndexes.push(index);
            }
            else {
                if (checkedPart.equation![index] === ']') {
                    closedBracketIndexes.push(index)
                }
            }
        }
        if (openBracketIndexes.length != closedBracketIndexes.length) {
            throw 'Problem part ' + String.fromCharCode(96 + checkedPart.order) + "'s equation has mismatching brackets in it resulting in incorrect variable names."
        }
        openBracketIndexes.forEach((value, index) => {
            if (closedBracketIndexes[index] < value) {
                throw 'Problem part ' + String.fromCharCode(96 + checkedPart.order) + "'s equation has mismatching brackets in it resulting in incorrect variable names."

            }
            var variableName = checkedPart.equation!.substring(value + 1, closedBracketIndexes[index])
            let foundVariableName = false
            if (variableName == "g") {
                foundVariableName = true
            }
            if (!foundVariableName) {
                for (let i = 0; i < variables.length; i++) {
                    if (variables[i].name == variableName) {
                        foundVariableName = true
                        break
                    }
                }
            }
            if (!foundVariableName) {
                throw 'Problem part ' + String.fromCharCode(96 + checkedPart.order) + "'s equation contains an invalid variable name. Variable names are case sensitive."
            }
        })
    }

    const checkPartTextForVariableNames = (checkedPart: CPPart, variables: Array<CPVariable>) => {
        var openBracketIndexes: Array<number> = []
        var closedBracketIndexes: Array<number> = []
        var textLength = checkedPart.text?.length ?? 0
        for (let index = 0; index < textLength; index++) {
            if (checkedPart.text![index] === '[') {
                openBracketIndexes.push(index);
            }
            else {
                if (checkedPart.text![index] === ']') {
                    closedBracketIndexes.push(index)
                }
            }
        }
        if (openBracketIndexes.length != closedBracketIndexes.length) {
            throw 'Problem part ' + String.fromCharCode(96 + checkedPart.order) + "'s text has mismatching brackets in it resulting in incorrect variable names."
        }
        openBracketIndexes.forEach((value, index) => {
            if (closedBracketIndexes[index] < value) {
                throw 'Problem part ' + String.fromCharCode(96 + checkedPart.order) + "'s text has mismatching brackets in it resulting in incorrect variable names."

            }
            var variableName = checkedPart.text!.substring(value + 1, closedBracketIndexes[index])
            let foundVariableName = false
            if (variableName == "g") {
                foundVariableName = true
            }
            if (!foundVariableName) {
                for (let i = 0; i < variables.length; i++) {
                    if (variables[i].name == variableName) {
                        foundVariableName = true
                        break
                    }
                }
            }
            if (!foundVariableName) {
                throw 'Problem part ' + String.fromCharCode(96 + checkedPart.order) + "'s text contains an invalid variable name. Variable names are case sensitive."
            }
        })
    }

    const checkProblemTextForVariableNames = (checkedProblem: CPProblem, variables: Array<CPVariable>) => {
        var openBracketIndexes: Array<number> = []
        var closedBracketIndexes: Array<number> = []
        for (let index = 0; index < checkedProblem.text!.length; index++) {
            if (checkedProblem.text![index] === '[') {
                openBracketIndexes.push(index);
            }
            else {
                if (checkedProblem.text![index] === ']') {
                    closedBracketIndexes.push(index)
                }
            }
        }
        if (openBracketIndexes.length != closedBracketIndexes.length) {
            throw 'Problem ' + checkedProblem.order + "'s text has mismatching brackets in it resulting in incorrect variable names."
        }
        openBracketIndexes.forEach((value, index) => {
            if (closedBracketIndexes[index] < value) {
                throw 'Problem ' + checkedProblem.order + "'s text has mismatching brackets in it resulting in incorrect variable names."

            }
            var variableName = checkedProblem.text!.substring(value + 1, closedBracketIndexes[index])
            let foundVariableName = false
            if (variableName == "g") {
                foundVariableName = true
            }
            if (!foundVariableName) {
                for (let i = 0; i < variables.length; i++) {
                    if (variables[i].name == variableName) {
                        foundVariableName = true
                        break
                    }
                }
            }
            if (!foundVariableName) {
                throw 'Problem ' + checkedProblem.order + "'s text contains an invalid variable name. Variable names are case sensitive."
            }
        })
    }

    const validatePartForSave = (part: CPPart, variables: Array<CPVariable>) => {
        // Prevent save without an equation. Tom wanted to be able to save without one
        //if (CPReactMethods.isNullOrWhiteSpace(part.equation)) {
        //    throw 'Problem part ' + String.fromCharCode(96 + part.order) + ' is missing an equation.'
        //}
        checkEquationForVariableNames(part, variables)
        checkPartTextForVariableNames(part, variables)
        if (!CPReactMethods.isNullOrWhiteSpace(part.answerLabel)) {
            if (part.answerLabel.length > 25) {
                throw "Problem part " + String.fromCharCode(96 + part.order) + "'s answer label is too long. The limit is 15 characters."
            }
        }
        if (!CPReactMethods.isNullOrWhiteSpace(part.unitsLabel)) {
            if (part.unitsLabel.length > 25) {
                throw "Problem part " + String.fromCharCode(96 + part.order) + "'s units label is too long. The limit is 15 characters."
            }
        }
        if (part.multipleChoice) {
            if (part.options == undefined || part.options.length <= 1) {
                throw 'Problem part ' + String.fromCharCode(96 + part.order) + ' is multiple choice, but has one or less options. All multiple choice parts must have at least two options.'
            }
            else {
                part.options.forEach(option => {
                    if (CPReactMethods.isNullOrWhiteSpace(option.text)) {
                        throw 'Problem part ' + String.fromCharCode(96 + part.order) + ' has an option without display text.'
                    }
                    if (CPReactMethods.isNullOrWhiteSpace(option.value.toString())) {
                        throw 'Problem part ' + String.fromCharCode(96 + part.order) + ' has an option without a value.'
                    }
                    if (isNaN(parseFloat(option.value.toString()))) {
                        throw 'Problem part ' + String.fromCharCode(96 + part.order) + ' has an option with a non number value. All option values must be numbers.'
                    }
                })
            }
        }
    }

    const validateProblemForSave = async (problem: CPProblem) => {
        var validProblem = true
        var errorMessage = ""
        await window.CPInstructor.checkProblem(problem).then(response => {
            if (!response.valid || !response.successful) {
                validProblem = false
                errorMessage = response.message
            }
        }).catch((error) => {
            validProblem = false
            errorMessage = error
        })
        if (!validProblem) {
            throw errorMessage
        }
        /*var validProblem = true;
        var errorMessage = "";
        if (validProblem && CPReactMethods.isNullOrWhiteSpace(problem.name)) {
            validProblem = false;
            errorMessage = "Problem must have a name."
        }
        if (validProblem && CPReactMethods.isNullOrWhiteSpace(problem.text)) {
            validProblem = false;
            errorMessage = "Problem must have problem text."
        }

        var testVariablesForSameName: Array<CPVariable> = []
        problem.variables.forEach(variable => {
            if (validProblem && variable.name == "g") {
                validProblem = false
                errorMessage = "The variable name g is a global variable and can't be used for a custom variable."
            }
            if (validProblem && testVariablesForSameName.find(v => v.name == variable.name)) {
                validProblem = false
                errorMessage = "All variables need to have a unique name."
            }
            testVariablesForSameName.push(variable)
            if (validProblem && CPReactMethods.isNullOrWhiteSpace(variable.name)) {
                validProblem = false
                errorMessage = "All variables need to have a name."
            }
            variable.es.forEach(exclusion => {
                if (validProblem && parseFloat(exclusion.min.toString()) > parseFloat(exclusion.max.toString())) {
                    validProblem = false
                    errorMessage = "All exclusions must have a min that is less than or equal to their max."
                }
            })
            variable.rs.forEach(range => {
                if (validProblem && parseFloat(range.min.toString()) > parseFloat(range.max.toString())) {
                    validProblem = false
                    errorMessage = "All ranges must have a min that is less than or equal to their max."
                }
                if (validProblem && parseFloat(range.inc.toString()) <= 0) {
                    validProblem = false
                    errorMessage = "All ranges must have an increment that is greater than or equal to zero."
                }
            })
            if (validProblem && !variable.isDependent) {
                try {
                    checkVariableForPossibleValues(variable)
                }
                catch (error) {
                    validProblem = false
                    // @ts-ignore
                    errorMessage = error
                }
            }
        })

        problem.variables.forEach(variable => {
            if (validProblem && variable.isDependent) {
                if (CPReactMethods.isNullOrWhiteSpace(variable.calculation)) {
                    validProblem = false
                    errorMessage = "Dependent variable " + variable.name + " is missing a calculation."
                }
                if (CPReactMethods.isNullOrUndefined(variable.precision)) {
                    validProblem = false
                    errorMessage = "Dependent variable " + variable.name + " is missing a precision."
                }
                checkDependentVariableCalculation(variable, problem.variables)
            }
        })

        try {
            if (validProblem) {
                checkProblemTextForVariableNames(problem, problem.variables)
            }
        }
        catch (error) {
            validProblem = false
            // @ts-ignore
            errorMessage = error
        }
        problem.links.forEach(link => {
            if (validProblem && CPReactMethods.isNullOrWhiteSpace(link.url)) {
                validProblem = false
                errorMessage = "All links must have a url."
            }
        })
        for (var part of problem.parts) {
            if (validProblem) {
                const response = await window.CPInstructor.checkPart(part, problem).then(response => {
                    if (!response.successful) {
                        validProblem = false;
                        errorMessage = response.message
                    }
                }).catch((error) => {
                    validProblem = false
                    errorMessage = error
                })
            }
        }
        if (!validProblem) {
            throw errorMessage
        }*/
    }

    const saveToAPI = (clone: boolean) => {
        if (!savingProblem) {
            var tags: Array<string> = []
            selectedTags.forEach(tag => tags.push(tag.value))
            var saveProblem = cloneDeep(initialCPProblem)
            saveProblem.id = id
            saveProblem.helpText = helpText
            saveProblem.image = image
            saveProblem.instanceId = problemInstanceId
            saveProblem.links = cloneDeep(links)
            saveProblem.name = name
            saveProblem.parts = removeInvalidDecimalsFromEquations(cloneDeep(parts))
            saveProblem.text = text
            saveProblem.variables = cloneDeep(variables)
            saveProblem.tags = tags
            saveProblem.difficulty = difficulty

            if (saveProblem.helpText == "<p></p>\n") {
                saveProblem.helpText = undefined
            }
            if (saveProblem.text == "<p></p>\n") {
                saveProblem.text = undefined
            }
            setSavingProblem(true)
            try {
                validateProblemForSave(saveProblem).then(async response => {

                    await window.CPInstructor.setProblem(saveProblem, clone, props.standAloneProblem, props.assignmentId).then(response => {
                        checkIfPartInstanceWasRemoved(response.data.parts, response.data.guid || "")
                        setInitialCPProblem(cloneDeep(response.data))
                        setLimitedEditing(response.limitedEditing)
                        setHelpText(response.data.helpText || "")
                        partHelpTextToBlocks = htmlToDraft(response.data.helpText || "")
                        setHelpTextEditorState(
                            () => EditorState.createWithContent(ContentState.createFromBlockArray(partHelpTextToBlocks.contentBlocks, partHelpTextToBlocks.entityMap)),
                        )
                        setId(response.data.id)
                        setImage(response.data.image)
                        setLinks(response.data.links)
                        setName(response.data.name || "")
                        setParts(cloneDeep(response.data.parts))
                        setText(response.data.text || "")
                        partTextToBlocks = htmlToDraft(response.data.text || "")
                        setTextEditorState(
                            () => EditorState.createWithContent(ContentState.createFromBlockArray(partTextToBlocks.contentBlocks, partTextToBlocks.entityMap)),
                        )
                        setVariables(cloneDeep(response.data.variables))
                        setProblemInstanceId(response.data.instanceId)
                        /*if (selectedPart) {
                            setSelectedPart(response.data.parts[selectedPart.order - 1])
                            setSelectedPartIndex(selectedPart.order)
                        }*/
                        var formattedTags: Array<{ label: string, value: string }> = []
                        if (response.data.tags) {
                            response.data.tags.forEach(tag => formattedTags.push({ label: tag, value: tag }))
                        }
                        setSelectedTags(formattedTags)
                        setDifficulty(response.data.difficulty)
                        setUserIsOwner(response.userIsOwner)
                        if (clone) {
                            setInMultiplePlaces(false)
                            setAnsweredInAssignment(false)
                        }
                        setLimitedEditing(response.limitedEditing)
                        var saveTime = new Date()
                        var hours = saveTime.getHours()
                        hours = (hours == 0 ? 12 : hours > 12 ? hours - 12 : hours)
                        var minutes: string | number = saveTime.getMinutes()
                        minutes = (minutes < 10 ? "0" + minutes : minutes.toString())
                        setLastTimeSaved(hours + ":" + minutes)
                        if (clone && props.standAloneProblem) {
                            window.location.replace('/calcpad/problem/edit/' + response.data.guid);
                        }
                    }).catch((error) => {
                        setWarningContent("" + (error.message ?? error))
                        // @ts-ignore
                        var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal"), { backdrop: true, keyboard: true, focus: true })
                        warningModal.show();
                    })
                }).finally(() => {
                    setSavingProblem(false);
                }).catch((error) => {
                    setWarningContent("" + (error.message ?? error))
                    // @ts-ignore
                    var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal"), { backdrop: true, keyboard: true, focus: true })
                    warningModal.show();
                    setSavingProblem(false);
                })
            }
            catch (error) {
                setSavingProblem(false)
                setWarningContent("" + error)
                // @ts-ignore
                var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal"), { backdrop: true, keyboard: true, focus: true })
                warningModal.show();
            }
        }
    }

    const removeInvalidDecimalsFromEquations = (parts: Array<CPPart>) => {
        parts.forEach(part => {
            while (part.equation[part.equation.length - 1] == ".") {
                part.equation = part.equation.substring(0, part.equation.length - 1)
            }
            for (let i = 0; i < part.equation.length; i++) {
                if (part.equation[i] == ".") {
                    if (!/^\d+$/.test(part.equation[i + 1])) {
                        part.equation = part.equation.slice(0, i) + part.equation.slice(i + 1)
                        i--
                    }
                }
            }
        })
        setParts(parts)
        return parts
    }

    const changeTagSelect = selectedOptions => {
        setSelectedTags(selectedOptions)
    }

    const changeDifficulty = selectedDifficulty => {
        setDifficulty(selectedDifficulty.value)
    }

    const backButton = () => {
        if (!props.standAloneProblem) {
            if (pendingChanges) {
                // @ts-ignore
                var returnToAssignmentBuilderModal = new bootstrap.Modal(document.getElementById("returnToAssignmentBuilderModal"), { backdrop: true, keyboard: true, focus: true })
                returnToAssignmentBuilderModal.show()
            }
            else {
                setIsLoading(true)
                props.returnProblemToAssignment!(initialCPProblem);
            }
        }
    }

    const previewProblem = () => {
        if (!CPReactMethods.isNullOrUndefined(id)) {
            var previewElement = document.getElementById("problemPreview")
            if (!CPReactMethods.isNullOrUndefined(previewElement)) {
                try {
                    setIsLoading(true)
                    if (props.standAloneProblem) {
                        // @ts-ignore
                        window.CPInstructor.previewProblem(previewElement, initialCPProblem.guid, initialCPProblem.courseGuid).then(response => {
                            setIsProblemPreview(true)
                        }).catch((error) => {
                            setIsProblemPreview(false)
                            setWarningContent(error.message)
                            // @ts-ignore
                            var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal"), { backdrop: true, keyboard: true, focus: true })
                            warningModal.show();
                        })
                    }
                    else {
                        // @ts-ignore
                        window.CPInstructor.previewMyProblem(previewElement, problemInstanceId, initialCPProblem.courseGuid).then(response => {
                            setIsProblemPreview(true)
                        }).catch((error) => {
                            setIsProblemPreview(false)
                            setWarningContent(error.message)
                            // @ts-ignore
                            var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal"), { backdrop: true, keyboard: true, focus: true })
                            warningModal.show();
                        })
                    }
                }
                catch (ex) {
                    // @ts-ignore
                    setWarningContent(ex)
                    // @ts-ignore
                    var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal"), { backdrop: true, keyboard: true, focus: true })
                    warningModal.show();
                    setIsProblemPreview(false)
                    setIsLoading(false)
                }
            }
        }
    }

    const returnFromPreviewProblem = () => {
        var problemPreviewDiv = document.getElementById("problemPreview")
        setIsProblemPreview(false)
        setIsLoading(false)
        // For some reason clearing this div makes the preview not initialize the second time
        /*while (problemPreviewDiv?.firstChild) {
            problemPreviewDiv.removeChild(assignmentPreviewDiv.firstChild)
        }*/
    }

    const addPart = partIndex => {
        var updatedParts = cloneDeep(parts)
        var newPart: CPPart = { id: undefined, points: undefined, text: "", equation: "", answerLabel: "", unitsLabel: "", options: undefined, multipleChoice: false, scientificNotation: false, calculateAsDouble: false, order: partIndex + 1 }
        updatedParts.splice(partIndex + 1, 0, newPart)
        updatedParts.forEach((part, index) => {
            part.order = index + 1;
        })
        setParts(updatedParts)
        setSelectedPartIndex(partIndex + 1)
    }

    const checkIfPartInstanceWasRemoved = (updatedParts: Array<CPPart>, problemGuid: string) => {
        if (!props.standAloneProblem && userIsOwner) {
            var missingPartInstance = false
            initialCPProblem.parts.forEach(part => {
                // If one of the original part instance ids is missing, one of them must've been deleted and we should warn the user that they didn't delete it from the original problem
                if (!missingPartInstance && part.instanceId != null) {
                    if (!updatedParts.some(updatedPart => updatedPart.instanceId == part.instanceId)) {
                        missingPartInstance = true
                    }
                }
            })
            if (missingPartInstance) {
                setWarningContent("You've removed a part from this problem. It has only been removed in this assignment. If you'd like to remove it from all versions of this assignment delete it in the <a target=\"_blank\" href=\"" + window.location.origin + "/calcpad/problem/edit/" + problemGuid + "\">standalone problem builder</a>.")
                // @ts-ignore
                var warningModal = new bootstrap.Modal(document.getElementById("problemWarningModal"), { backdrop: true, keyboard: true, focus: true })
                warningModal.show();
            }
        }
    }

    return (
        <>
            {isProblemPreview ?
                <div className="container-fluid px-3">
                    {props.standAloneProblem ?
                        <BreadcrumbComponent
                            breadcrumbs={[{ text: "Problem Builder", function: selectedPartIndex != undefined ? () => { /*setSelectedPart(undefined);*/ setSelectedPartIndex(undefined); returnFromPreviewProblem() } : returnFromPreviewProblem }, { text: selectedPartIndex != undefined ? "Part Builder" : "", function: selectedPartIndex != undefined ? () => returnFromPreviewProblem() : undefined }, { text: "Problem Preview" }]}
                        />
                        :
                        <BreadcrumbComponent
                            breadcrumbs={[{ text: "Assignment Builder", function: () => backButton() }, { text: "Problem Builder", function: selectedPartIndex != undefined ? () => { /*setSelectedPart(undefined);*/ setSelectedPartIndex(undefined); returnFromPreviewProblem() } : returnFromPreviewProblem }, { text: selectedPartIndex != undefined ? "Part Builder" : "", function: selectedPartIndex != undefined ? () => returnFromPreviewProblem() : undefined }, { text: "Problem Preview" }]}
                        />}
                    <button type="button" className="btn btn-primary mx-2 mb-2" onClick={() => { setSelectedPartIndex(undefined); returnFromPreviewProblem() }} > Back to Problem Builder</button>
                    {selectedPartIndex != undefined ?
                        <button type="button" className="btn btn-primary mx-2 mb-2" onClick={returnFromPreviewProblem}> Back to Part Builder</button> : ""
                    }
                </div > :
                <div className={savingProblem || isLoading ? "container-fluid px-3 busy" : "container-fluid px-3"}>
                    <div className="row">
                        <div className="row">
                            <div className="col">
                                {props.standAloneProblem ?
                                    <BreadcrumbComponent
                                        breadcrumbs={[{ text: "Problem Builder", function: selectedPartIndex != undefined ? () => { /*setSelectedPart(undefined);*/ setSelectedPartIndex(undefined); } : undefined }, { text: selectedPartIndex != undefined ? "Part Builder" : "" }]}
                                    />
                                    :
                                    <BreadcrumbComponent
                                        breadcrumbs={[{ text: "Assignment Builder", function: () => backButton() }, { text: "Problem Builder", function: selectedPartIndex != undefined ? () => { /*setSelectedPart(undefined);*/ setSelectedPartIndex(undefined) } : undefined }, { text: selectedPartIndex != undefined ? "Part Builder" : "" }]}
                                    />}
                                {!userIsOwner ?
                                    <div className="alert alert-warning alert-dismissible fade show" role="alert">
                                        <b>You are not the owner of this problem. {!props.standAloneProblem ? "You may only edit help text and help links. " : ""}Clone this problem if you would like to edit your own copy.</b>
                                        <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                                    </div>
                                    :
                                    inMultiplePlaces ?
                                        <div className="alert alert-warning alert-dismissible fade show" role="alert">
                                            <b>This problem is used in multiple places. Any changes to this problem will affect other instances of this problem. Clone this problem if you only want to change this instance of the problem.</b>
                                            <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                                        </div> : ""
                                }
                                {assignmentIsShared ?
                                    <div className="alert alert-warning alert-dismissible fade show" role="alert">
                                        <b>This assignment is linked to a task in another course. Any changes to this assignment or its problems will affect other instances of this assignment. Clone this assignment from the assignment builder if you only want to change this instance of the assignment.</b>
                                        <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                                    </div> : ""
                                }
                                {answeredInAssignment ?
                                    <div className="alert alert-warning alert-dismissible fade show" role="alert">
                                        <b>Answers have already been submitted for this problem no changes that can affect correctness are allowed.</b>
                                        <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                                    </div>
                                    : (limitedEditing && userIsOwner) ?
                                        <div className="alert alert-warning alert-dismissible fade show" role="alert">
                                            <b>Answers have been submitted to this problem in a different assignment. Clone this problem to make changes without affecting another assignment.</b>
                                            <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                                        </div> : ""}
                                {!props.standAloneProblem || userIsOwner ?
                                    <button type="button" className={pendingChanges ? "btn btn-primary mx-2 mb-2" : "btn btn-outline-primary mx-2 mb-2"} onClick={() => saveToAPI(false)}>{savingProblem ? "Saving" : "Save Problem"}</button>
                                    : ""}
                                {selectedPartIndex != undefined ?
                                    <>
                                        <button type="button" className="btn btn-primary mx-2 mb-2" onClick={() => { /*setSelectedPart(undefined);*/ setSelectedPartIndex(undefined) }}>Back to Problem Builder</button>
                                        {parts.length > 1 ?
                                            <>
                                                <button type="button" disabled={selectedPartIndex < 1} className="btn btn-primary mx-2 mb-2" onClick={() => { setSelectedPartIndex(selectedPartIndex - 1); }}>Previous Part</button>
                                                {selectedPartIndex >= parts.length - 1 ?
                                                    <button type="button" className="btn btn-primary mx-2 mb-2" onClick={() => addPart(selectedPartIndex)}>Add Part</button>
                                                    : <button type="button" disabled={selectedPartIndex >= parts.length - 1} className="btn btn-primary mx-2 mb-2" onClick={() => { setSelectedPartIndex(selectedPartIndex + 1);  }}>Next Part</button>
                                                }
                                            </>
                                            : <button type="button" className="btn btn-primary mx-2 mb-2" onClick={() => addPart(selectedPartIndex)}>Add Part</button>
                                        }
                                    </> :
                                    !props.standAloneProblem ?
                                        <button type="button" className="btn btn-primary mx-2 mb-2" onClick={() => backButton()}>Back to Assignment Builder</button>
                                        : ""
                                }
                                {(!userIsOwner || inMultiplePlaces) ?
                                    <button type="button" className="btn btn-primary mx-2 mb-2" data-bs-toggle="modal" data-bs-target="#cloneProblemConfirmationModal">Clone Problem</button> : ""
                                }
                                <button type="button" disabled={pendingChanges} className="btn btn-primary mx-2 mb-2" onClick={() => previewProblem()}>Preview Problem</button>
                                {lastTimeSaved ? <label className="mx-2 mb-2"> Problem Last Saved at {lastTimeSaved}</label> : ""}
                            </div>
                        </div>
                        <div className="col-xxl-7">
                            {selectedPartIndex != undefined ?
                                <PartBuilder
                                    //key={selectedPartIndex}
                                    selectedPart={parts[selectedPartIndex]}
                                    //setSelectedPart={setSelectedPart}
                                    selectedPartIndex={selectedPartIndex}
                                    setSelectedPartIndex={setSelectedPartIndex}
                                    savePart={savePart}
                                    variables={variables}
                                    userIsOwner={userIsOwner}
                                    limitedEditing={limitedEditing}
                                    assignmentLimitedEditing={props.assignmentLimitedEditing ?? false}
                                />
                                :
                                <>
                                    <div className="row">
                                        <div className="col">
                                            <div className="row">
                                                <b>Problem Name:</b>
                                            </div>
                                            <div className="row">
                                                <div className="col">
                                                    <input disabled={!userIsOwner} className="form-control" type="text" maxLength={100} onChange={e => setName(e.currentTarget.value)} value={name} />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col">
                                            <b>Problem Text:</b>
                                            <RichTextEditorComponent
                                                editorState={textEditorState}
                                                changeEditor={changeTextEditor}
                                                placeholder="Problem Text"
                                                showSymbolDropdown={true}
                                                maxCharacters={750}
                                                readOnly={!userIsOwner}
                                                variables={variables}
                                            />
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col">
                                            {/* Image URL Setting */}
                                            <div className="row">
                                                <div className="col">
                                                    <b>Image URL:</b>
                                                    <input disabled={!userIsOwner} className="form-control" type="url" onChange={e => changeImageURL(e.currentTarget)} value={image.url ?? ""} />
                                                    <div className="row">
                                                        <div className="col-3" /><div className="col-6">{image.url ? <img className="cpproblemimage mx-auto d-block mw-100" src={image.url}></img> : ""}</div><div className="col-3" />
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Image Allignment Setting */}
                                            <div className="row">
                                                <b>Align Image:&emsp;</b>
                                                <div className="col">
                                                    <div className="form-check form-check-inline">
                                                        <input role="button" disabled={!userIsOwner} className="form-check-input" type="radio" name="alignImageRadio" id="alignAbove" value={CPImageAlignment.Above} checked={image.align == CPImageAlignment.Above} onChange={e => changeImageAlign(e.currentTarget)} />
                                                        <label className="form-check-label" htmlFor="alignAbove">Above</label>
                                                    </div>
                                                    <div className="form-check form-check-inline">
                                                        <input role="button" disabled={!userIsOwner} className="form-check-input" type="radio" name="alignImageRadio" id="alignBelow" value={CPImageAlignment.Below} checked={image.align == CPImageAlignment.Below} onChange={e => changeImageAlign(e.currentTarget)} />
                                                        <label className="form-check-label" htmlFor="alignBelow">Below</label>
                                                    </div>
                                                    <div className="form-check form-check-inline">
                                                        <input role="button" disabled={!userIsOwner} className="form-check-input" type="radio" name="alignImageRadio" id="alignMiddle" value={CPImageAlignment.Middle} checked={image.align == CPImageAlignment.Middle} onChange={e => changeImageAlign(e.currentTarget)} />
                                                        <label className="form-check-label" htmlFor="alignMiddle">Middle</label>
                                                    </div>
                                                    <div className="form-check form-check-inline">
                                                        <input role="button" disabled={!userIsOwner} className="form-check-input" type="radio" name="alignImageRadio" id="alignLeft" value={CPImageAlignment.Left} checked={image.align == CPImageAlignment.Left} onChange={e => changeImageAlign(e.currentTarget)} />
                                                        <label className="form-check-label" htmlFor="alignLeft">Left</label>
                                                    </div>
                                                    <div className="form-check form-check-inline">
                                                        <input role="button" disabled={!userIsOwner} className="form-check-input" type="radio" name="alignImageRadio" id="alignRight" value={CPImageAlignment.Right} checked={image.align == CPImageAlignment.Right} onChange={e => changeImageAlign(e.currentTarget)} />
                                                        <label className="form-check-label" htmlFor="alignRight">Right</label>
                                                    </div>
                                                </div>
                                            </div>
                                            {/* Image Size Setting */}
                                            <div className="row">
                                                <b>Image Size:&emsp;</b>
                                                <div className="col">
                                                    <div className="form-check form-check-inline">
                                                        <input role="button" disabled={!userIsOwner} className="form-check-input" type="radio" name="imageSizeRadio" id="sizeDefault" value={CPImageSize.Default} checked={image.imageSize == CPImageSize.Default} onChange={e => changeImageSize(e.currentTarget)} />
                                                        <label className="form-check-label" htmlFor="sizeDefault">Default</label>
                                                    </div>
                                                    <div className="form-check form-check-inline">
                                                        <input role="button" disabled={!userIsOwner} className="form-check-input" type="radio" name="imageSizeRadio" id="sizeSmall" value={CPImageSize.Small} checked={image.imageSize == CPImageSize.Small} onChange={e => changeImageSize(e.currentTarget)} />
                                                        <label className="form-check-label" htmlFor="sizeSmall">Small</label>
                                                    </div>
                                                    <div className="form-check form-check-inline">
                                                        <input role="button" disabled={!userIsOwner} className="form-check-input" type="radio" name="imageSizeRadio" id="sizeMedium" value={CPImageSize.Medium} checked={image.imageSize == CPImageSize.Medium} onChange={e => changeImageSize(e.currentTarget)} />
                                                        <label className="form-check-label" htmlFor="sizeMedium">Medium</label>
                                                    </div>
                                                    <div className="form-check form-check-inline">
                                                        <input role="button" disabled={!userIsOwner} className="form-check-input" type="radio" name="imageSizeRadio" id="sizeLarge" value={CPImageSize.Large} checked={image.imageSize == CPImageSize.Large} onChange={e => changeImageSize(e.currentTarget)} />
                                                        <label className="form-check-label" htmlFor="sizeLarge">Large</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <PartsList
                                        userIsOwner={userIsOwner}
                                        limitedEditing={limitedEditing}
                                        parts={parts}
                                        setParts={setParts}
                                        addPart={addPart}
                                        setSelectedPartIndex={(partIndex: number) => { setSelectedPartIndex(partIndex); }}
                                        removePartInstance={props.removePartInstance}
                                        standaloneProblem={props.standAloneProblem}
                                        answeredInAssignment={answeredInAssignment ?? false} />
                                    <b>Help Text:</b>
                                    <RichTextEditorComponent
                                        editorState={helpTextEditorState}
                                        changeEditor={changeHelpTextEditor}
                                        showSymbolDropdown={true}
                                        placeholder="Help Text"
                                        maxCharacters={500}
                                        readOnly={false}
                                    />

                                    <LinksList
                                        links={links}
                                        setLinks={setLinks}
                                        disabled={props.standAloneProblem && !userIsOwner}
                                    />
                                    <div className="row">
                                        <div className="col">
                                            <div className="row">
                                                <b>Tags:</b>
                                            </div>
                                            <div className="row">
                                                <CreateableSelect
                                                    isDisabled={!userIsOwner}
                                                    isMulti
                                                    onChange={changeTagSelect}
                                                    options={tagOptions}
                                                    value={selectedTags}
                                                    placeholder="Select Tags..."
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="row">
                                        <div className="col">
                                            <div className="row">
                                                <b>Difficulty:</b>
                                            </div>
                                            <Select
                                                isDisabled={!userIsOwner}
                                                options={difficultyOptions}
                                                placeholder="Select Difficulty"
                                                value={difficulty ? difficultyOptions[difficulty - 1] : { label: "Select Difficulty", value: 0 }}
                                                onChange={changeDifficulty}
                                            />
                                        </div>
                                    </div>

                                </>
                            }
                        </div>
                        <div className="col-xxl-5">
                            <VariableComponent
                                variables={variables}
                                setVariables={setVariables}
                                userIsOwner={userIsOwner}
                                limitedEditing={limitedEditing}
                            />
                        </div>
                    </div>
                    <WarningModalComponent
                        itemContent={warningContent}
                        itemId="problemWarningModal"
                    />
                    {inMultiplePlaces || !userIsOwner ?
                        <ConfirmationModalComponent
                            itemId="cloneProblemConfirmationModal"
                            itemContent="Do you want to clone your own copy of this problem?"
                            accept={() => { saveToAPI(true) }}
                        /> : ""}
                    {!props.standAloneProblem ?
                        <ConfirmationModalComponent
                            itemId="returnToAssignmentBuilderModal"
                            itemContent="There are unsaved changes to this problem. Do you want to cancel these changes?"
                            accept={() => { setIsLoading(true); props.returnProblemToAssignment!(initialCPProblem) }}
                        />
                        : ""}
                </div>}
            <div className="container-fluid px-3" hidden={!isProblemPreview} id="problemPreview" />
        </>
    );
}



export default ProblemBuilder;